import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Group,
  Paper,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconClock,
  IconEye,
  IconInfoCircle,
  IconRefresh,
  IconSearch,
  IconSourceCode,
  IconUsers,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";

import { OverviewCard } from "@pages/AdminDashboard/components/OverviewCard";

import { WithDashboardNavbar } from "@layouts/WithDashboardNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { api } from "@utils/api";
import { useAuth } from "@contexts/auth";

import {
  useGetDoctorEngagement,
  useGetDoctorFunnel,
} from "@hooks/queries/dashboard/useGetDoctorFunnel";
import {
  DoctorFunnelPlatform,
  useGetDoctorProfileActivity,
} from "@hooks/queries/dashboard/useGetDoctorProfileActivity";

import { PaymentConversion } from "./PaymentConversion";


const formatDuration = (value: number) => {
  if (!value) return "0s";
  if (value < 60) return `${Math.round(value)}s`;
  return `${Math.floor(value / 60)}m ${Math.round(value % 60)}s`;
};

const MetricHeader = ({
  label,
  description,
  align = "right",
}: {
  label: string;
  description: string;
  align?: "left" | "right";
}) => (
  <Group
    gap={4}
    wrap="nowrap"
    justify={align === "right" ? "flex-end" : "flex-start"}>
    <Text span inherit>
      {label}
    </Text>
    <Tooltip label={description} multiline maw={280} withArrow>
      <IconInfoCircle
        aria-label={description}
        role="img"
        tabIndex={0}
        size={14}
        color="var(--mantine-color-gray-6)"
      />
    </Tooltip>
  </Group>
);

interface ProfileActivityProps {
  userRole?: "admin" | "staff";
}

export const ProfileActivity = ({
  userRole = "admin",
}: ProfileActivityProps) => {
  const { t, i18n } = useTranslation();
  const { permissions, roles } = useAuth();
  const canViewAll = permissions["reports.view"] === "all";
  const isAdmin = roles.includes("admin");
  const queryClient = useQueryClient();
  const [numDays, setNumDays] = useState(3);
  const [search, setSearch] = useState("");
  const [funnelSearch, setFunnelSearch] = useState("");
  const [funnelSort, setFunnelSort] = useState("booked");
  const [funnelDays, setFunnelDays] = useState(30);
  const [funnelPlatform, setFunnelPlatform] =
    useState<DoctorFunnelPlatform>("website");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { data, isLoading, isError } = useGetDoctorProfileActivity(
    numDays,
    funnelPlatform
  );
  const { data: funnelData, isLoading: isFunnelLoading } =
    useGetDoctorFunnel(funnelDays, funnelPlatform);
  const { data: doctorEngagement } = useGetDoctorEngagement(
    funnelDays as 7 | 30 | 90,
    !canViewAll && roles.includes("doctor")
  );

  const doctors = useMemo(() => {
    const query = canViewAll ? search.trim().toLowerCase() : "";
    if (!query) return data?.doctors ?? [];
    return (data?.doctors ?? []).filter(
      doctor =>
        doctor.name.toLowerCase().includes(query) ||
        doctor.slug.toLowerCase().includes(query) ||
        doctor.specialization?.toLowerCase().includes(query)
    );
  }, [data?.doctors, search, canViewAll]);

  const funnelDoctors = useMemo(() => {
    const query = canViewAll ? funnelSearch.trim().toLowerCase() : "";
    const rows = (funnelData?.doctors ?? []).filter(
      doctor =>
        !query ||
        doctor.name.toLowerCase().includes(query) ||
        doctor.slug.toLowerCase().includes(query) ||
        doctor.specialization?.toLowerCase().includes(query)
    );
    return [...rows].sort((a, b) => {
      if (funnelSort === "doctor") return a.name.localeCompare(b.name);
      if (funnelSort === "views") {
        return b.funnel.doctorProfileViewed - a.funnel.doctorProfileViewed;
      }
      if (funnelSort === "paid") {
        return b.funnel.paymentCompleted - a.funnel.paymentCompleted;
      }
      return b.funnel.bookingCompleted - a.funnel.bookingCompleted;
    });
  }, [funnelData?.doctors, funnelSearch, funnelSort, canViewAll]);

  const refresh = async () => {
    setIsRefreshing(true);
    try {
      await api.get("analytics/doctor-profiles/clarity", {
        params: { num_days: numDays, platform: funnelPlatform, refresh: true },
      });
      await queryClient.invalidateQueries({
        queryKey: ["dashboard", "doctorProfileActivity"],
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const frictionTotal =
    (data?.totals.rageClicks ?? 0) +
    (data?.totals.deadClicks ?? 0) +
    (data?.totals.errorClicks ?? 0);
  const coverageUnavailable = data?.coverage.status === "unavailable";
  const metricValue = (value?: number) =>
    coverageUnavailable ? undefined : value ?? 0;
  const trackingStartedAt = data?.coverage.trackingStartedAt
    ? new Date(data.coverage.trackingStartedAt).toLocaleString(i18n.language)
    : undefined;
  const funnelStages = funnelData
    ? [
        {
          label: t("profile-activity-profile-views"),
          value: funnelData.totals.doctorProfileViewed,
          browserTracked: true,
        },
        {
          label: t("profile-activity-service-selected"),
          value: funnelData.totals.serviceSelected,
          browserTracked: true,
        },
        {
          label: t("profile-activity-slot-selected"),
          value: funnelData.totals.slotSelected,
          browserTracked: true,
        },
        {
          label: t("profile-activity-booking-started"),
          value: funnelData.totals.bookingStarted,
          browserTracked: true,
        },
        {
          label: t("profile-activity-booked"),
          value: funnelData.totals.bookingCompleted,
          browserTracked: false,
        },
        {
          label: t("profile-activity-paid-among-bookings"),
          value: funnelData.totals.paymentCompleted,
          browserTracked: false,
        },
      ]
    : [];
  const funnelViews = funnelData?.totals.doctorProfileViewed ?? 0;
  const funnelCoverageComplete = Boolean(funnelData?.trackingStartedAt);
  const browserMetric = (value: number) => value;
  const funnelTrackingStartedAt = funnelData?.trackingStartedAt
    ? new Date(funnelData.trackingStartedAt).toLocaleString(
        i18n.language
      )
    : undefined;
  const acquisitionRows = data?.acquisitionTotals ?? [];
  const referrerRows = data?.topReferrers ?? [];
  const acquisitionSessions = acquisitionRows.reduce(
    (total, row) => total + row.sessions,
    0
  );

  return (
    <WithSidebar hasDashboardNavbar hasBackground={false}>
      <WithDashboardNavbar userRole={userRole}>
        <Stack gap="lg" pt="lg" pb={{ base: 160, md: 128 }}>
          <Group justify="space-between" align="flex-end" wrap="wrap">
            <Stack gap={4}>
              <Title order={2} fz={20} fw={600}>
                {canViewAll
                  ? t("profile-activity-admin-title")
                  : t("profile-activity-doctor-title")}
              </Title>
              <Text c="dimmed" fz={14}>
                {t("profile-activity-subtitle")}
              </Text>
            </Stack>

            <Group gap="sm">
              <SegmentedControl
                value={String(numDays)}
                onChange={value => setNumDays(Number(value))}
                data={[
                  { label: "24h", value: "1" },
                  { label: "48h", value: "2" },
                  { label: "72h", value: "3" },
                ]}
              />
              {isAdmin && (
                <Tooltip label={t("profile-activity-refresh-tooltip")}>
                  <ActionIcon
                    aria-label={t("profile-activity-refresh-aria")}
                    variant="default"
                    size={36}
                    loading={isRefreshing}
                    onClick={refresh}>
                    <IconRefresh size={18} />
                  </ActionIcon>
                </Tooltip>
              )}
            </Group>
          </Group>

          {!isLoading && data && !data.configured && (
            <Alert
              color="orange"
              icon={<IconAlertTriangle size={18} />}
              title={t("profile-activity-clarity-required")}>
              {isAdmin ? (
                <>
                  {t("profile-activity-add-token-prefix")}{" "}
                  <Text span ff="monospace">CLARITY_API_TOKEN</Text>{" "}
                  {t("profile-activity-add-token-suffix")}
                </>
              ) : (
                t("profile-activity-contact-admin")
              )}
            </Alert>
          )}

          {!isLoading &&
            data?.configured &&
            data.coverage.status !== "complete" && (
              <Alert
                color={
                  data.coverage.status === "unknown" ? "blue" : "orange"
                }
                icon={
                  data.coverage.status === "unknown" ? (
                    <IconInfoCircle size={18} />
                  ) : (
                    <IconAlertTriangle size={18} />
                  )
                }
                title={
                  data.coverage.status === "partial"
                    ? t("profile-activity-partial-title")
                    : data.coverage.status === "unavailable"
                      ? t("profile-activity-unavailable-title")
                      : t("profile-activity-unconfirmed-title")
                }>
                {data.coverage.status === "partial" &&
                  t("profile-activity-partial-message", {
                    date: trackingStartedAt,
                    percentage: data.coverage.percentage,
                  })}
                {data.coverage.status === "unavailable" &&
                  t("profile-activity-unavailable-message", {
                    date: trackingStartedAt,
                  })}
                {data.coverage.status === "unknown" &&
                  (isAdmin
                    ? t("profile-activity-unconfirmed-admin")
                    : t("profile-activity-unconfirmed-doctor"))}
              </Alert>
            )}

          {isError && (
            <Alert
              color="red"
              icon={<IconAlertTriangle size={18} />}
              title={t("profile-activity-load-error-title")}>
              {t("profile-activity-load-error-message")}
            </Alert>
          )}

          <SimpleGrid cols={{ base: 1, xs: 2, lg: 4 }} spacing="md">
            <OverviewCard
              title={t("profile-activity-profile-sessions")}
              amount={metricValue(data?.totals.sessions)}
              icon={<IconEye size={20} />}
              isLoading={isLoading}
            />
            <OverviewCard
              title={t("profile-activity-visitors")}
              amount={metricValue(data?.totals.users)}
              icon={<IconUsers size={20} />}
              isLoading={isLoading}
            />
            <OverviewCard
              title={t("profile-activity-friction-signals")}
              amount={metricValue(frictionTotal)}
              icon={<IconAlertTriangle size={20} />}
              isLoading={isLoading}
            />
            <OverviewCard
              title={
                canViewAll
                  ? t("profile-activity-active-profiles")
                  : t("profile-activity-pages-per-session")
              }
              amount={
                coverageUnavailable
                  ? undefined
                  : canViewAll
                    ? data?.doctors.length ?? 0
                    : data?.doctors[0]?.pagesPerSession ?? 0
              }
              icon={<IconClock size={20} />}
              isLoading={isLoading}
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            <Paper withBorder p="md" radius="md">
              <Stack gap={2} mb="md">
                <Group gap="xs">
                  <IconSourceCode size={18} />
                  <Text fw={600}>
                    {t("profile-activity-traffic-sources")}
                  </Text>
                </Group>
                <Text fz={13} c="dimmed">
                  {t("profile-activity-traffic-sources-subtitle")}
                </Text>
              </Stack>

              {acquisitionRows.length === 0 ? (
                <Text c="dimmed" fz={14} py="lg" ta="center">
                  {t("profile-activity-no-acquisition-data")}
                </Text>
              ) : (
                <ScrollArea>
                  <Table
                    miw={600}
                    verticalSpacing="sm"
                    horizontalSpacing="md"
                    highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>
                          {t("profile-activity-source")}
                        </Table.Th>
                        <Table.Th>
                          {t("profile-activity-medium")}
                        </Table.Th>
                        <Table.Th ta="right">
                          {t("profile-activity-sessions")}
                        </Table.Th>
                        <Table.Th ta="right">
                          {t("profile-activity-visitors")}
                        </Table.Th>
                        <Table.Th ta="right">
                          {t("profile-activity-share")}
                        </Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {acquisitionRows.map(row => (
                        <Table.Tr key={`${row.source}-${row.medium}`}>
                          <Table.Td>
                            <Text fz={14} fw={500}>
                              {row.source}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge variant="light" color="gray">
                              {row.medium}
                            </Badge>
                          </Table.Td>
                          <Table.Td ta="right">{row.sessions}</Table.Td>
                          <Table.Td ta="right">{row.users}</Table.Td>
                          <Table.Td ta="right">
                            {acquisitionSessions > 0
                              ? `${Math.round(
                                  (row.sessions / acquisitionSessions) * 100
                                )}%`
                              : "0%"}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </Paper>

            <Paper withBorder p="md" radius="md">
              <Stack gap={2} mb="md">
                <Text fw={600}>{t("profile-activity-referrers")}</Text>
                <Text fz={13} c="dimmed">
                  {t("profile-activity-referrers-subtitle")}
                </Text>
              </Stack>

              {referrerRows.length === 0 ? (
                <Text c="dimmed" fz={14} py="lg" ta="center">
                  {t("profile-activity-no-referrer-data")}
                </Text>
              ) : (
                <ScrollArea>
                  <Table
                    miw={360}
                    verticalSpacing="sm"
                    horizontalSpacing="md"
                    highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>
                          {t("profile-activity-referrer")}
                        </Table.Th>
                        <Table.Th ta="right">
                          {t("profile-activity-sessions")}
                        </Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {referrerRows.map(row => (
                        <Table.Tr key={row.domain}>
                          <Table.Td>
                            <Text fz={14} lineClamp={1}>
                              {row.domain}
                            </Text>
                          </Table.Td>
                          <Table.Td ta="right">{row.sessions}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </Paper>
          </SimpleGrid>

          <Paper withBorder p="md" radius="md">
            <Group justify="space-between" align="center" mb="md" wrap="wrap">
              <Stack gap={2}>
                <Text fw={600}>{t("profile-activity-booking-funnel")}</Text>
                <Text fz={13} c="dimmed">
                  {t("profile-activity-funnel-subtitle")}
                </Text>
              </Stack>
              <Group gap="sm">
                <SegmentedControl
                  value={String(funnelDays)}
                  onChange={value => setFunnelDays(Number(value))}
                  data={[
                    { label: "7d", value: "7" },
                    { label: "30d", value: "30" },
                    { label: "90d", value: "90" },
                  ]}
                />
                <Select
                  aria-label={t("profile-activity-platform")}
                  value={funnelPlatform}
                  onChange={value =>
                    setFunnelPlatform(
                      (value as DoctorFunnelPlatform | null) ?? "website"
                    )
                  }
                  data={[
                    {
                      label: t("profile-activity-platform-website"),
                      value: "website",
                    },
                    {
                      label: t("profile-activity-platform-mobile"),
                      value: "patient_mobile",
                    },
                    { label: "WhatsApp", value: "whatsapp" },
                    { label: "Kiko", value: "kiko" },
                    {
                      label: t("profile-activity-platform-all"),
                      value: "all",
                    },
                  ]}
                  w={190}
                />
                {isFunnelLoading && <Badge variant="light">Loading</Badge>}
              </Group>
            </Group>

            {funnelTrackingStartedAt && (
              <Alert
                mb="md"
                color="blue"
                icon={<IconInfoCircle size={18} />}
                title={t("profile-activity-funnel-recent-title")}>
                {funnelTrackingStartedAt
                  ? t("profile-activity-funnel-recent-date", {
                      date: funnelTrackingStartedAt,
                    })
                  : isAdmin
                    ? t("profile-activity-funnel-recent-admin")
                    : t("profile-activity-funnel-recent-doctor")}
              </Alert>
            )}

            <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }} spacing="md">
              {funnelStages.map(stage => (
                <Stack key={stage.label} gap={4}>
                  <Text fz={12} c="dimmed">
                    {stage.label}
                  </Text>
                  <Text fz={22} fw={600}>
                    {stage.browserTracked
                      ? browserMetric(stage.value)
                      : stage.value}
                  </Text>
                  <Text fz={11} c="dimmed">
                    {stage.browserTracked &&
                    funnelCoverageComplete &&
                    funnelViews > 0
                      ? t("profile-activity-percent-of-views", {
                          percentage: Math.round(
                            (stage.value / funnelViews) * 100
                          ),
                        })
                      : stage.browserTracked
                        ? t("profile-activity-coverage-incomplete")
                        : t("profile-activity-emr-confirmed")}
                  </Text>
                </Stack>
              ))}
            </SimpleGrid>

            {!canViewAll && doctorEngagement && (
              <>
                <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md" mt="xl">
                  {[
                    [t("profile-activity-share-actions"), doctorEngagement.attribution_funnel.share_intents],
                    [t("profile-activity-attributed-visitors"), doctorEngagement.attribution_funnel.unique_visitors],
                    [t("profile-activity-attributed-bookings"), doctorEngagement.attribution_funnel.bookings],
                    [t("profile-activity-attributed-payments"), doctorEngagement.attribution_funnel.payments],
                  ].map(([label, value]) => (
                    <Paper key={String(label)} withBorder radius="md" p="sm">
                      <Text fz={12} c="dimmed">{label}</Text>
                      <Text fz={20} fw={600}>{value}</Text>
                    </Paper>
                  ))}
                </SimpleGrid>
                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" mt="md">
                  {[
                    [
                      t("profile-activity-share-to-view"),
                      doctorEngagement.attribution_funnel.share_intents
                        ? doctorEngagement.attribution_funnel.unique_visitors /
                          doctorEngagement.attribution_funnel.share_intents
                        : 0,
                    ],
                    [
                      t("profile-activity-view-to-book"),
                      doctorEngagement.attribution_funnel.unique_visitors
                        ? doctorEngagement.attribution_funnel.bookings /
                          doctorEngagement.attribution_funnel.unique_visitors
                        : 0,
                    ],
                    [
                      t("profile-activity-book-to-pay"),
                      doctorEngagement.attribution_funnel.bookings
                        ? doctorEngagement.attribution_funnel.payments /
                          doctorEngagement.attribution_funnel.bookings
                        : 0,
                    ],
                  ].map(([label, rate]) => (
                    <Paper key={String(label)} withBorder radius="md" p="sm">
                      <Text fz={12} c="dimmed">{label}</Text>
                      <Text fz={20} fw={600}>
                        {Math.round(Number(rate) * 100)}%
                      </Text>
                    </Paper>
                  ))}
                </SimpleGrid>
              </>
            )}

            <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md" mt="xl">
              {[
                ["Channels", funnelData?.breakdowns.channel ?? []],
                ["Sources", funnelData?.breakdowns.source ?? []],
                ["Media", funnelData?.breakdowns.medium ?? []],
                ["Campaigns", funnelData?.breakdowns.campaign ?? []],
                ["Referrer domains", funnelData?.breakdowns.referrer_domain ?? []],
                ["Entry points", funnelData?.breakdowns.entry_point ?? []],
              ].map(([title, rows]) => (
                <Paper key={String(title)} withBorder radius="md" p="sm">
                  <Text fw={600} mb="xs">{title as string}</Text>
                  <Stack gap={6}>
                    {(rows as Array<{ value: string; count: number }>).length ?
                      (rows as Array<{ value: string; count: number }>).slice(0, 8).map(row => (
                        <Group key={row.value} justify="space-between">
                          <Text fz={13}>{row.value}</Text>
                          <Badge variant="light">{row.count}</Badge>
                        </Group>
                      )) : <Text fz={13} c="dimmed">No attributed data</Text>}
                  </Stack>
                </Paper>
              ))}
            </SimpleGrid>

            <Group
              justify="space-between"
              align="flex-end"
              mt="xl"
              mb="md"
              wrap="wrap">
              <Stack gap={2}>
                <Text fw={600}>
                  {t("profile-activity-doctor-breakdown")}
                </Text>
                <Text fz={13} c="dimmed">
                  {t("profile-activity-doctor-breakdown-subtitle")}
                </Text>
              </Stack>
              {canViewAll && (
                <Group gap="sm" w={{ base: "100%", md: "auto" }} wrap="wrap">
                  <TextInput
                    value={funnelSearch}
                    onChange={event =>
                      setFunnelSearch(event.currentTarget.value)
                    }
                    leftSection={<IconSearch size={16} />}
                    placeholder={t("profile-activity-search")}
                    w={{ base: "100%", sm: 280 }}
                  />
                  <Select
                    value={funnelSort}
                    onChange={value => setFunnelSort(value ?? "booked")}
                    label={t("profile-activity-sort-by")}
                    data={[
                      {
                        label: t("profile-activity-booked"),
                        value: "booked",
                      },
                      {
                        label: t("profile-activity-profile-views"),
                        value: "views",
                      },
                      {
                        label: t("profile-activity-paid-among-bookings"),
                        value: "paid",
                      },
                      {
                        label: t("profile-activity-doctor"),
                        value: "doctor",
                      },
                    ]}
                    w={{ base: "100%", sm: 210 }}
                  />
                </Group>
              )}
            </Group>

            {funnelDoctors.length === 0 ? (
              <Text c="dimmed" fz={14} py="lg" ta="center">
                {t("profile-activity-no-funnel-doctors")}
              </Text>
            ) : (
              <ScrollArea>
                <Table
                  miw={1180}
                  verticalSpacing="sm"
                  horizontalSpacing="md"
                  highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t("profile-activity-doctor")}</Table.Th>
                      <Table.Th ta="right">
                        {t("profile-activity-profile-views")}
                      </Table.Th>
                      <Table.Th ta="right">
                        {t("profile-activity-service-selected")}
                      </Table.Th>
                      <Table.Th ta="right">
                        {t("profile-activity-slot-selected")}
                      </Table.Th>
                      <Table.Th ta="right">
                        {t("profile-activity-booking-started")}
                      </Table.Th>
                      <Table.Th ta="right">
                        {t("profile-activity-booked")}
                      </Table.Th>
                      <Table.Th ta="right">
                        {t("profile-activity-paid-among-bookings")}
                      </Table.Th>
                      <Table.Th ta="right">
                        {t("profile-activity-view-to-booked")}
                      </Table.Th>
                      <Table.Th ta="right">
                        {t("profile-activity-booked-to-paid")}
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {funnelDoctors.map(doctor => {
                      const views = doctor.funnel.doctorProfileViewed;
                      const booked = doctor.funnel.bookingCompleted;
                      const paid = doctor.funnel.paymentCompleted;
                      return (
                        <Table.Tr key={doctor.doctorId}>
                          <Table.Td>
                            <Group gap="sm" wrap="nowrap">
                              <Avatar
                                src={doctor.profileImage}
                                name={doctor.name}
                                radius="xl"
                              />
                              <Stack gap={1}>
                                <Text fz={14} fw={500}>
                                  {doctor.name}
                                </Text>
                                <Text fz={12} c="dimmed">
                                  {doctor.specialization || doctor.slug}
                                </Text>
                              </Stack>
                            </Group>
                          </Table.Td>
                          <Table.Td ta="right">
                            {browserMetric(views)}
                          </Table.Td>
                          <Table.Td ta="right">
                            {browserMetric(doctor.funnel.serviceSelected)}
                          </Table.Td>
                          <Table.Td ta="right">
                            {browserMetric(doctor.funnel.slotSelected)}
                          </Table.Td>
                          <Table.Td ta="right">
                            {browserMetric(doctor.funnel.bookingStarted)}
                          </Table.Td>
                          <Table.Td ta="right">{booked}</Table.Td>
                          <Table.Td ta="right">{paid}</Table.Td>
                          <Table.Td ta="right">
                            {funnelCoverageComplete && views > 0
                              ? `${Math.round((booked / views) * 100)}%`
                              : t("profile-activity-not-tracked")}
                          </Table.Td>
                          <Table.Td ta="right">
                            {booked > 0
                              ? `${Math.round((paid / booked) * 100)}%`
                              : "0%"}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            )}
          </Paper>

          <PaymentConversion days={funnelDays} platform={funnelPlatform}
            doctors={funnelData?.doctors ?? []} canViewAll={canViewAll} />

          <Paper withBorder p="md" radius="md">
            <Group justify="space-between" mb="md" align="center" wrap="wrap">
              <Stack gap={2}>
                <Text fw={600}>{t("profile-activity-performance")}</Text>
                <Text fz={13} c="dimmed">
                  {data?.fetchedAt
                    ? t("profile-activity-last-imported", {
                        date: new Date(data.fetchedAt).toLocaleString(
                          i18n.language
                        ),
                      })
                    : t("profile-activity-no-snapshot")}
                </Text>
              </Stack>
              {canViewAll && (
                <TextInput
                  value={search}
                  onChange={event => setSearch(event.currentTarget.value)}
                  leftSection={<IconSearch size={16} />}
                  placeholder={t("profile-activity-search")}
                  w={{ base: "100%", sm: 300 }}
                />
              )}
            </Group>

            {isLoading ? (
              <Stack>
                {Array.from({ length: 5 }).map((_, index) => (
                  <Skeleton key={index} h={48} />
                ))}
              </Stack>
            ) : doctors.length === 0 ? (
              <Stack align="center" py={48} gap={6}>
                <Text fw={500}>
                  {t("profile-activity-no-activity")}
                </Text>
                <Text c="dimmed" fz={14} ta="center">
                  {coverageUnavailable
                    ? t("profile-activity-tracking-not-started")
                    : t("profile-activity-empty-help")}
                </Text>
              </Stack>
            ) : (
              <ScrollArea>
                <Table
                  miw={1050}
                  verticalSpacing="sm"
                  horizontalSpacing="md"
                  highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>
                        <MetricHeader
                          label={t("profile-activity-doctor")}
                          description={t("profile-activity-doctor-help")}
                          align="left"
                        />
                      </Table.Th>
                      <Table.Th ta="right">
                        <MetricHeader
                          label={t("profile-activity-sessions")}
                          description={t("profile-activity-sessions-help")}
                        />
                      </Table.Th>
                      <Table.Th ta="right">
                        <MetricHeader
                          label={t("profile-activity-visitors")}
                          description={t("profile-activity-visitors-help")}
                        />
                      </Table.Th>
                      <Table.Th ta="right">
                        <MetricHeader
                          label={t("profile-activity-engagement")}
                          description={t("profile-activity-engagement-help")}
                        />
                      </Table.Th>
                      <Table.Th ta="right">
                        <MetricHeader
                          label={t("profile-activity-scroll-depth")}
                          description={t("profile-activity-scroll-depth-help")}
                        />
                      </Table.Th>
                      <Table.Th ta="right">
                        <MetricHeader
                          label={t("profile-activity-rage-clicks")}
                          description={t("profile-activity-rage-clicks-help")}
                        />
                      </Table.Th>
                      <Table.Th ta="right">
                        <MetricHeader
                          label={t("profile-activity-dead-clicks")}
                          description={t("profile-activity-dead-clicks-help")}
                        />
                      </Table.Th>
                      <Table.Th ta="right">
                        <MetricHeader
                          label={t("profile-activity-errors")}
                          description={t("profile-activity-errors-help")}
                        />
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {doctors.map(doctor => {
                      const errors = doctor.errorClicks + doctor.scriptErrors;
                      return (
                        <Table.Tr key={doctor.profileId}>
                          <Table.Td>
                            <Group gap="sm" wrap="nowrap">
                              <Avatar
                                src={doctor.profileImage}
                                name={doctor.name}
                                radius="xl"
                              />
                              <Stack gap={1}>
                                <Text fz={14} fw={500}>
                                  {doctor.name}
                                </Text>
                                <Text fz={12} c="dimmed">
                                  {doctor.specialization || doctor.slug}
                                </Text>
                              </Stack>
                            </Group>
                          </Table.Td>
                          <Table.Td ta="right">{doctor.sessions}</Table.Td>
                          <Table.Td ta="right">{doctor.users}</Table.Td>
                          <Table.Td ta="right">
                            {formatDuration(doctor.engagementTime)}
                          </Table.Td>
                          <Table.Td ta="right">
                            {doctor.scrollDepth
                              ? `${doctor.scrollDepth.toFixed(1)}%`
                              : "—"}
                          </Table.Td>
                          <Table.Td ta="right">
                            <Badge
                              variant="light"
                              color={doctor.rageClicks > 0 ? "orange" : "gray"}>
                              {doctor.rageClicks}
                            </Badge>
                          </Table.Td>
                          <Table.Td ta="right">{doctor.deadClicks}</Table.Td>
                          <Table.Td ta="right">
                            <Badge
                              variant="light"
                              color={errors > 0 ? "red" : "gray"}>
                              {errors}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            )}
          </Paper>
        </Stack>
      </WithDashboardNavbar>
    </WithSidebar>
  );
};
